import React, {
  useEffect,
  useImperativeHandle,
  useState,
  forwardRef,
  useRef
} from 'react';
import { SlashMenuProps } from './slashCommand.types';

interface SlashMenuImperativeHandle {
  onKeyDown: ({ event }: { event: KeyboardEvent }) => boolean;
}

const SlashMenu = forwardRef<SlashMenuImperativeHandle, SlashMenuProps>(
  function SlashMenu({ items, command }, ref) {
    const [selectedIndex, setSelectedIndex] = useState(0);
    const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

    const selectItem = (index: number) => {
      const item = items[index];

      if (item) {
        command(item);
      }
    };

    const upHandler = () => {
      setSelectedIndex(
        prevIndex => (prevIndex + items.length - 1) % items.length
      );
    };

    const downHandler = () => {
      setSelectedIndex(prevIndex => (prevIndex + 1) % items.length);
    };

    const enterHandler = () => {
      selectItem(selectedIndex);
    };

    useEffect(() => {
      setSelectedIndex(0);

      itemRefs.current.length = items.length;
    }, [items]);

    useEffect(() => {
      if (!items.length) return;

      const selectedItem = itemRefs.current[selectedIndex];

      if (selectedItem) {
        selectedItem.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }, [selectedIndex, items.length]);

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: KeyboardEvent }) => {
        if (event.key === 'ArrowUp') {
          upHandler();
          event.preventDefault();

          return true;
        }

        if (event.key === 'ArrowDown') {
          downHandler();
          event.preventDefault();

          return true;
        }

        if (event.key === 'Enter') {
          enterHandler();

          return true;
        }

        return false;
      }
    }));

    return (
      <ul className="slash-command-menu" role="listbox">
        {items.length ? (
          items.map((item, index) => (
            <li
              key={index}
              role="option"
              aria-selected={index === selectedIndex}
            >
              <button
                className={index === selectedIndex ? 'is-selected' : ''}
                ref={el => (itemRefs.current[index] = el)}
                onClick={() => selectItem(index)}
                tabIndex={-1}
                type="button"
              >
                <span className="slash-command-menu-icon">{item.icon}</span>
                {item.title}
              </button>
            </li>
          ))
        ) : (
          <li className="item">No result</li>
        )}
      </ul>
    );
  }
);

export default SlashMenu;
